import { useState } from 'react';
import type { FormEvent } from 'react';
import { ACTIONS, APP_ROLES } from '@erp-attach/shared';
import type { Action, AppRole, GrantEffect } from '@erp-attach/shared';
import { useApi } from '../../api/ApiProvider';
import { useApiQuery } from '../../api/useApiQuery';
import { formatDateTime } from '../../lib/format';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Chip } from '../../components/ui/Chip';
import { EmptyState } from '../../components/ui/EmptyState';
import { ErrorBanner } from '../../components/ui/ErrorBanner';
import { Spinner } from '../../components/ui/Spinner';

const GRANTABLE: readonly Action[] = ACTIONS.filter((a) => a !== 'grant' && a !== 'audit');

export function FolderGrantsAdmin() {
  const api = useApi();
  const [folderFilter, setFolderFilter] = useState('');
  const [applied, setApplied] = useState('');
  const query = useApiQuery((client, signal) => client.admin.folderGrants(applied || undefined, signal), [applied]);

  const [folderId, setFolderId] = useState('');
  const [subject, setSubject] = useState<'role' | 'user'>('role');
  const [appRole, setAppRole] = useState<AppRole>('Sales.Branch');
  const [oid, setOid] = useState('');
  const [actions, setActions] = useState<Action[]>(['read']);
  const [effect, setEffect] = useState<GrantEffect>('allow');
  const [expiresAt, setExpiresAt] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const create = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.admin.createFolderGrant({
        folderId: folderId.trim(),
        appRole: subject === 'role' ? appRole : undefined,
        oid: subject === 'user' ? oid.trim() : undefined,
        actions,
        effect,
        expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
        reason: reason.trim(),
      });
      setReason('');
      setApplied(folderId.trim());
      setFolderFilter(folderId.trim());
      query.reload();
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setBusy(false);
    }
  };

  const valid = folderId.trim().length > 0 && actions.length > 0 && reason.trim().length >= 5 && (subject === 'role' || oid.trim().length > 0);

  return (
    <div className="space-y-4">
      <Card title="Add exception grant">
        <p className="mb-3 text-xs text-muted-foreground">
          Grants are the 20% case (spec §3). Default access comes from grant templates by role; use this only for a named
          exception, with an expiry wherever possible.
        </p>
        <form onSubmit={create} className="grid gap-3 md:grid-cols-2">
          <label className="block text-xs">
            <span className="font-medium text-muted-foreground">Folder id</span>
            <input className="gsl-input mt-1 !py-1.5 text-sm" value={folderId} onChange={(e) => setFolderId(e.target.value)} required />
          </label>
          <div className="block text-xs">
            <span className="font-medium text-muted-foreground">Subject</span>
            <div className="mt-1 flex gap-2">
              <select className="gsl-input !py-1.5 text-sm" value={subject} onChange={(e) => setSubject(e.target.value as 'role' | 'user')} aria-label="Subject kind">
                <option value="role">App role</option>
                <option value="user">User (oid)</option>
              </select>
              {subject === 'role' ? (
                <select className="gsl-input !py-1.5 text-sm" value={appRole} onChange={(e) => setAppRole(e.target.value as AppRole)} aria-label="App role">
                  {APP_ROLES.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              ) : (
                <input className="gsl-input !py-1.5 text-sm" value={oid} onChange={(e) => setOid(e.target.value)} placeholder="Entra object id" aria-label="User oid" />
              )}
            </div>
          </div>
          <fieldset className="md:col-span-2">
            <legend className="text-xs font-medium text-muted-foreground">Actions</legend>
            <div className="mt-1 flex flex-wrap gap-3">
              {GRANTABLE.map((a) => (
                <label key={a} className="inline-flex items-center gap-1 text-sm">
                  <input
                    type="checkbox"
                    className="accent-[var(--color-gsl-red)]"
                    checked={actions.includes(a)}
                    onChange={(e) => setActions((cur) => (e.target.checked ? [...cur, a] : cur.filter((x) => x !== a)))}
                  />
                  {a}
                </label>
              ))}
            </div>
          </fieldset>
          <label className="block text-xs">
            <span className="font-medium text-muted-foreground">Effect</span>
            <select className="gsl-input mt-1 !py-1.5 text-sm" value={effect} onChange={(e) => setEffect(e.target.value as GrantEffect)}>
              <option value="allow">allow</option>
              <option value="deny">deny</option>
            </select>
          </label>
          <label className="block text-xs">
            <span className="font-medium text-muted-foreground">Expires (optional)</span>
            <input type="datetime-local" className="gsl-input mt-1 !py-1.5 text-sm" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
          </label>
          <label className="block text-xs md:col-span-2">
            <span className="font-medium text-muted-foreground">Reason (required, audited)</span>
            <input className="gsl-input mt-1 !py-1.5 text-sm" value={reason} onChange={(e) => setReason(e.target.value)} required />
          </label>
          <div className="md:col-span-2">
            <Button type="submit" disabled={!valid} busy={busy}>
              Create grant
            </Button>
          </div>
        </form>
        {error ? (
          <div className="mt-3">
            <ErrorBanner error={error} />
          </div>
        ) : null}
      </Card>

      <Card
        title="Existing grants"
        actions={
          <form
            className="flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              setApplied(folderFilter.trim());
            }}
          >
            <input
              className="gsl-input !py-1 text-sm"
              placeholder="Filter by folder id"
              value={folderFilter}
              onChange={(e) => setFolderFilter(e.target.value)}
              aria-label="Filter by folder id"
            />
            <Button type="submit" size="sm" variant="secondary">
              Filter
            </Button>
          </form>
        }
        padded={false}
      >
        {query.loading ? (
          <div className="p-4">
            <Spinner label="Loading grants" />
          </div>
        ) : query.error ? (
          <div className="p-4">
            <ErrorBanner error={query.error} />
          </div>
        ) : (query.data ?? []).length === 0 ? (
          <div className="p-4">
            <EmptyState title="No exception grants" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className="gsl-th">Folder</th>
                  <th className="gsl-th">Subject</th>
                  <th className="gsl-th">Actions</th>
                  <th className="gsl-th">Effect</th>
                  <th className="gsl-th">Expires</th>
                  <th className="gsl-th">Granted</th>
                </tr>
              </thead>
              <tbody>
                {(query.data ?? []).map((g) => (
                  <tr key={g.id} className="gsl-row border-b border-border align-top last:border-b-0">
                    <td className="px-4 py-2 text-xs">
                      <div>{g.folderPath ?? g.folderId}</div>
                      {g.folderPath ? <div className="gsl-num text-muted-foreground">{g.folderId}</div> : null}
                    </td>
                    <td className="px-4 py-2">{g.appRole ?? g.upn ?? g.oid}</td>
                    <td className="px-4 py-2">
                      <div className="flex flex-wrap gap-1">
                        {g.actions.map((a) => (
                          <Chip key={a} variant="neutral">
                            {a}
                          </Chip>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      <Chip variant={g.effect === 'allow' ? 'success' : 'danger'}>{g.effect}</Chip>
                    </td>
                    <td className="px-4 py-2 text-xs">{g.expiresAt ? formatDateTime(g.expiresAt) : 'never'}</td>
                    <td className="px-4 py-2 text-xs">
                      <div>{g.grantedByUpn ?? g.grantedByOid}</div>
                      <div className="text-muted-foreground">{formatDateTime(g.createdAt)}</div>
                      <div className="mt-1 italic">{g.reason}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
